import {
    Component,
    NgZone,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { bindPosition, Placement, Strategy } from '@cmps/cdk';
import { CmpsTemplatePortal, CmpsTemplatePortalHandle } from '@cmps/ng-uikit';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { AxisData } from '../chart/axis-data';
import { AxisOptions, AxisPointMouseEvent } from '../chart/chart';

import { PlotPoint } from './scatterplot.model';
import { ScatterPlotService } from './scatterplot.services';

@Component({
    selector: '[cmpsScatterPlot]',
    templateUrl: './scatterplot.component.html',
    host: {
        class: 'cmps-scatterplot',
    },
})
export class ScatterPlotComponent implements OnInit {
    behaviourTarget: string = '';
    outcomeTarget: string = '';

    behaviourTargetName: string;
    outcomeTargetName: string;

    selectedUser: string = '';
    selectedRole: string = '';
    selectedDate: string = '';
    startDate: string = '';
    plotData: PlotPoint[] = new Array<PlotPoint>();

    /** @private */ _chartOptions: AxisOptions;
    /** @private */ _chartData: AxisData;

    @ViewChild('callout') private readonly _callout: TemplateRef<any>;
    private _hdl: CmpsTemplatePortalHandle<any>;
    private _hdlSubj: Subject<void>;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _portal: CmpsTemplatePortal,
        private readonly _scatterPlotService: ScatterPlotService,
    ) {}

    ngOnInit(): void {
        this.plotData = this._scatterPlotService.getPlotData();
        this.selectedUser = this._scatterPlotService.selectedUser;
        this.selectedRole = this._scatterPlotService.selectedRole;
        this.selectedDate = this._scatterPlotService.selectedDate;
        const startDate = new Date(this._scatterPlotService.selectedDate);
        startDate.setDate(startDate.getDate() - 29);
        this.startDate = startDate.toString();

        this.behaviourTargetName =
            this._scatterPlotService.selectedBehaviorName;
        this.outcomeTargetName = this._scatterPlotService.selectedOutcomeName;

        this.behaviourTarget = this._scatterPlotService.userBehaviourTarget;
        this.outcomeTarget = this._scatterPlotService.userOutcomeTarget;

        this._renderChart();
    }

    getMaxXTick(): number {
        let maxXTick: number = 0;
        const plotData = this._scatterPlotService.getPlotData();
        if (plotData) {
            plotData.forEach((point: PlotPoint) => {
                const x: number = Number(point.x);
                if (x > maxXTick) {
                    maxXTick = x;
                }
            });
            const behaviorTarget = this._scatterPlotService.userBehaviourTarget;
            const resp =
                maxXTick > Number(behaviorTarget)
                    ? maxXTick + 1
                    : Number(behaviorTarget) + 50;
            return resp;
        } else {
            return 0;
        }
    }

    getMaxYTick(): number {
        const plotData = this._scatterPlotService.getPlotData();
        let maxYTick: number = 0;

        if (plotData) {
            plotData.forEach((point: PlotPoint) => {
                const y: number = Number(point.y);
                if (y > maxYTick) {
                    maxYTick = y;
                }
            });
            const outcomeTarget = this._scatterPlotService.userOutcomeTarget;
            const resp =
                maxYTick > Number(outcomeTarget)
                    ? maxYTick + 1
                    : Number(outcomeTarget) + 50;
            return resp;
        } else {
            return 0;
        }
    }

    /** @private */
    _onPointMouseOver($event: AxisPointMouseEvent): void {
        this._onPointMouseOut();
        const { hdl, cancelSubj } = this._makePortal<any>(
            this._callout,
            { data: $event },
            $event.el as HTMLElement,
            'bottom-left',
        );
        this._hdl = hdl;
        this._hdlSubj = cancelSubj;
    }

    /** @private */
    _onPointMouseOut(): void {
        this._hdlSubj?.next();
        this._hdlSubj?.complete();
        this._hdlSubj = null;

        this._hdl?.destroy();
        this._hdl = null;
    }

    private _renderChart(): void {
        this._chartOptions = {
            rect: { width: '100%', height: 500, top: 20 },
            xDomain: () => [0, this.getMaxXTick()],
            yDomain: () => [0, this.getMaxYTick()],
            strokeColor: (d, i) => d[i].color,
            yAxisLabel: (d, i) => d[i].text,
        };

        const data = this._scatterPlotService.getPlotData2();
        if ((data.paths?.[0]?.points?.length ?? 0) === 0) {
            return;
        }

        this._chartData = data;
    }

    private _makePortal<T>(
        templateRef: TemplateRef<T>,
        context: T,
        target: HTMLElement,
        placement: Placement,
    ): { hdl: CmpsTemplatePortalHandle<T>; cancelSubj: Subject<void> } {
        const hdl = this._portal.create<T>(
            templateRef,
            this._viewContainerRef,
            context,
        );
        hdl.ref.detectChanges();

        const cancelSubj = new Subject<void>();

        /// //
        const posFn = bindPosition(
            Strategy.ADJACENT,
            hdl.wrapper,
            target,
            placement,
            (pos) => {
                Object.keys(pos.style).forEach((k) => {
                    hdl.wrapper.style[k] = pos.style[k];

                    // bindPosition should support padding the size of the target rect, or overriding the target rect
                    if (k === 'top') {
                        hdl.wrapper.style.top = `${
                            parseInt(pos.style[k]) + 10
                        }px`;
                    }
                });
                hdl.wrapper.style.zIndex = '10000';
            },
            cancelSubj,
        );
        this._ngZone.onStable
            .pipe(
                tap(() => {
                    posFn();
                }),
                takeUntil(cancelSubj),
            )
            .subscribe();
        /// //

        return { hdl, cancelSubj };
    }
}
